import MainNav from "./main-nav";
import UserNav from "./user-nav";
import React, { useState, useEffect } from "react";
import WarningModal from "../clients/details/screen-resolution-warning";
import { Steps } from "intro.js-react";
import "intro.js/introjs.css"; // Import Intro.js styles
import "../../css/intro-custom.css";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie"; // Cookie library
import { useAuth } from "../../lib/AuthContext";
import XanoAPI from "../../lib/XanoAPI";
import logo from "../../images/logo.png";

// Utility function to get steps based on role
const getIntroSteps = (role: string): Array<any> => {
  const adminSteps = [
    {
      id: "main-nav",
      element: ".main-nav",
      intro:
        "This is the main navigation where you can access key sections of the app.",
    },
    {
      id: "user-nav",
      element: ".user-nav",
      intro: "Here you can manage your account settings and preferences.",
    },
    {
      id: "client-dashboard",
      intro:
        "Let’s take you to the client dashboard where you can manage clients.",
    },
    {
      id: "add-client",
      element: ".add-client-button",
      intro:
        "Click this button to create a new client. This will open a modal.",
    },
    {
      id: "my-team",
      intro:
        "Now, let's navigate to the My Team section to manage your agency's team members.",
    },
    {
      id: "invite-team-member",
      element: ".invite-team-member",
      intro: "Click this button to invite a new team member to your agency.",
    },
    // {
    //   id: "add-new-role",
    //   element: "#add-new-role",
    //   intro:
    //     "You can also add a new role for your team members by clicking this button.",
    // },
  ];

  const agentSteps = [
    {
      id: "main-nav",
      element: ".main-nav",
      intro:
        "This is the main navigation where you can access key sections of the app.",
    },
    {
      id: "user-nav",
      element: ".user-nav",
      intro: "Here you can manage your account settings and preferences.",
    },
    {
      id: "client-dashboard",
      intro:
        "Let’s take you to the client dashboard where you can manage clients.",
    },
    {
      id: "add-client",
      element: ".add-client-button",
      intro:
        "Click this button to create a new client. This will open a modal.",
    },
  ];

  return role === "admin" ? adminSteps : agentSteps;
};

export default function Header() {
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIntroEnabled, setIsIntroEnabled] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(
    parseInt(Cookies.get("current_intro_step") || "0", 10)
  );
  const [introSteps, setIntroSteps] = useState<Array<any>>([]);
  const navigate = useNavigate();
  const api = XanoAPI.getInstance();

  const checkScreenWidth = () => {
    if (window.innerWidth < 767) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    
    // Check if screen is smaller than 1024px for mobile menu
    setIsMobile(window.innerWidth < 1024);
    // Close mobile menu when resizing to desktop
    if (window.innerWidth >= 1024) {
      setIsMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    checkScreenWidth();
    window.addEventListener("resize", checkScreenWidth);

    return () => {
      window.removeEventListener("resize", checkScreenWidth);
    };
  }, []);

  const initializeIntro = () => {
    const currentUser = auth?.currentUser;

    if (!currentUser) return false;

    const userPermissions = currentUser.user_permissions || [];
    const isAdmin = userPermissions.some(
      (perm: PermissionType) => perm.role === "admin"
    );
    const isAgent = userPermissions.some(
      (perm: PermissionType) => perm.role === "agent"
    );

    // Admin role takes precedence
    if (isAdmin) {
      setIntroSteps(getIntroSteps("admin"));
    } else if (isAgent) {
      setIntroSteps(getIntroSteps("agent"));
    }

    // Run walkthrough if not yet completed
    return isAdmin || isAgent ? !currentUser.walkthrough_created : false;
  };

  useEffect(() => {
    const shouldRunWalkthrough = initializeIntro();
    setIsIntroEnabled(shouldRunWalkthrough);

    // Clear cookie if walkthrough shouldn't run
    if (!shouldRunWalkthrough) {
      Cookies.remove("current_intro_step");
    }
  }, [auth?.currentUser]);

  const handleIntroChange = (nextStep: number) => {
    setCurrentStepIndex(nextStep);

    // Save the current step to the cookie
    Cookies.set("current_intro_step", String(nextStep), { expires: 7 });

    if (introSteps.length > 0) {
      const currentStep = introSteps[nextStep];

      // Use step IDs to handle specific actions
      switch (currentStep?.id) {
        case "client-dashboard":
          navigate("/clients/");
          break;
        case "my-team":
          navigate("/account/team/");
          break;
        case "invite-team-member":
          console.log("Invite team member button triggered");
          // document.querySelector(".invite-team-member")?.click();
          break;
        case "add-new-role":
          console.log("Add new role button triggered");
          // document.querySelector(".add-new-role")?.click();
          break;
        default:
          break;
      }
    }
  };

  const handleWalkthroughCompletion = async () => {
    const userId = api.getUserId();

    try {
      const user = await api.getTeamMemberById(userId);

      if (user?.walkthrough_created) return;
      await api.updateUser(userId, {
        ...user,
        walkthrough_created: true,
      });
      console.log("Walkthrough status updated successfully.");
    } catch (error) {
      console.error("Failed to update walkthrough status:", error);
    }
  };

  const handleIntroExit = () => {
    console.log("Intro.js exited");
    setIsIntroEnabled(false);

    // Clear cookie and update walkthrough status
    Cookies.remove("current_intro_step");
    handleWalkthroughCompletion();
  };

  return (
    <div>
      <WarningModal isOpen={isOpen} onClose={() => setIsOpen(false)} />

      {/* Intro.js Steps */}
      <Steps
        enabled={isIntroEnabled}
        steps={introSteps}
        initialStep={currentStepIndex}
        onChange={handleIntroChange}
        onExit={handleIntroExit}
        options={{
          showProgress: true,
          showStepNumbers: true,
          exitOnOverlayClick: false,
          disableInteraction: true,
        }}
      />

      <div className="border-b bg-white z-[9999] fixed w-[100vw]">
        <div className="flex h-16 items-center px-2">
          {/* Logo - only visible on mobile */}
          <Link to="/" className="lg:hidden mr-4">
            <img src={logo} alt="logo" width="50" height="50" />
          </Link>
          
          {/* Hamburger menu button - visible on screens < 1024px */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Desktop Navigation - visible on screens >= 1024px */}
          <MainNav className="main-nav mx-6 hidden lg:flex" />
          
          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[10000] lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Mobile Menu Sidebar */}
          <div
            className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-[10001] lg:hidden overflow-hidden ${
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="p-0 overflow-hidden h-full">
              <MainNav 
                className="main-nav flex-col" 
                onLinkClick={() => setIsMobileMenuOpen(false)}
              />
            </div>
          </div>

          {/* User Nav - always visible */}
          <div className="ml-auto flex items-center space-x-4 user-nav">
            <UserNav />
          </div>
        </div>
      </div>
    </div>
  );
}
